/**
 * wallets.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin wallet management — session-gated, full sidebar, design-matched to
 * users.jsx / AdminDashboard.
 * Route: /admin/wallets
 *
 * Tables used:
 *   wallets       (id, user_id, balance, currency, updated_at, created_at)
 *   users         (id, first_name, last_name, email, status)
 *   transactions  (user_id, wallet_id, type, amount, status, note, created_at)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut,CreditCard, MapPin,
  // page
  Search, RefreshCw, X, CheckCircle2, AlertCircle, XCircle,
  ChevronUp, ChevronDown, ArrowUpRight, ArrowDownRight, Minus,
  DollarSign, TrendingUp, TrendingDown, Users,
} from "lucide-react";

const PER_PAGE = 15;

// ─── Sidebar nav (identical to users.jsx) ────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
    ],
  },
   {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
    ],
  },
];

// ─── Styles (design-matched to users.jsx, prefixed wl-) ──────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --border-glow:  rgba(99,102,241,0.3);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
    --transition-smooth: cubic-bezier(0.4, 0, 0.2, 1);
  }

  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; scroll-behavior: smooth; }

  .wl-shell {
    display: flex; height: 100vh; overflow: hidden;
    background: var(--bg-base); position: relative;
  }
  .wl-shell::before {
    content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* Sidebar */
  .ad-sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    display: flex; flex-direction: column;
    background: var(--bg-surface); border-right: 1px solid var(--border);
    position: relative; z-index: 10;
    height: 100vh; overflow: hidden;
  }
  .ad-sidebar::after {
    content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }
  .ad-logo {
    display: flex; align-items: center; gap: 11px;
    padding: 0 18px; height: var(--topbar-h);
    border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .ad-logo-icon {
    width: 32px; height: 32px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35), 0 0 32px rgba(99,102,241,0.15);
  }
  .ad-logo-text strong {
    font-size: 13.5px; font-weight: 800; letter-spacing: -0.4px;
    background: linear-gradient(to right, #eeeeff, #a5b4fc);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .ad-logo-text span {
    font-size: 9.5px; color: var(--text-muted); display: block;
    margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500;
  }
  .ad-nav {
    flex: 1; overflow-y: auto; overflow-x: hidden;
    padding: 18px 10px; display: flex; flex-direction: column; gap: 22px;
    scroll-behavior: smooth; -webkit-overflow-scrolling: touch;
    min-height: 0;
  }
  .ad-nav::-webkit-scrollbar { width: 3px; }
  .ad-nav::-webkit-scrollbar-track { background: transparent; }
  .ad-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.2); border-radius: 99px; }
  .ad-nav::-webkit-scrollbar-thumb:hover { background: rgba(99,102,241,0.4); }
  .ad-nav-section-label {
    font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--text-muted); padding: 0 10px; margin-bottom: 5px;
  }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 10px; border-radius: var(--radius-sm);
    font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    text-decoration: none; transition: all 0.18s ease; position: relative;
  }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%);
    color: var(--accent-light); font-weight: 600;
    box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15);
  }
  .ad-nav-link.active::before {
    content: ''; position: absolute; left: 0; top: 18%; bottom: 18%;
    width: 2.5px; border-radius: 99px;
    background: linear-gradient(to bottom, var(--accent), var(--purple));
    box-shadow: 0 0 10px var(--accent-glow), 0 0 20px rgba(99,102,241,0.2);
  }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }
  .ad-user-card {
    flex-shrink: 0; border-top: 1px solid var(--border); padding: 12px 10px;
    background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%);
  }
  .ad-user-inner {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 10px; border-radius: var(--radius-sm); transition: background 0.15s;
  }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2));
    border: 1.5px solid rgba(99,102,241,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: var(--accent-light); flex-shrink: 0;
    box-shadow: 0 0 12px rgba(99,102,241,0.15);
  }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span   { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn {
    width: 28px; height: 28px; border-radius: 7px;
    background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); transition: all 0.15s;
  }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); box-shadow: 0 0 12px rgba(251,113,133,0.15); }

  /* Page column */
  .wl-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; }

  /* Topbar */
  .wl-topbar {
    flex-shrink: 0; height: var(--topbar-h);
    background: rgba(10,10,22,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; position: relative;
  }
  .wl-topbar::after {
    content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%);
  }
  .wl-topbar-left { display: flex; align-items: center; gap: 14px; }
  .wl-topbar-badge {
    display: flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px;
    background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2);
    font-size: 10px; font-weight: 700; color: var(--accent-light);
    letter-spacing: 0.5px; text-transform: uppercase;
  }
  .wl-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .wl-topbar-sub   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
  .wl-topbar-right { display: flex; align-items: center; gap: 10px; }
  .wl-btn {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px;
    border-radius: var(--radius-sm); font-size: 11.5px; font-weight: 500;
    cursor: pointer; transition: all 0.18s; font-family: var(--font-sans);
    border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary);
  }
  .wl-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); box-shadow: 0 0 12px rgba(99,102,241,0.1); }

  /* Main */
  .wl-main {
    flex: 1; overflow-y: auto; padding: 26px 28px;
    display: flex; flex-direction: column; gap: 22px;
  }
  .wl-main::-webkit-scrollbar { width: 4px; }
  .wl-main::-webkit-scrollbar-track { background: transparent; }
  .wl-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat grid */
  .wl-stat-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; }
  .wl-stat {
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: var(--radius-md); padding: 20px;
    display: flex; flex-direction: column; gap: 14px;
    position: relative; overflow: hidden;
    transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s;
  }
  .wl-stat::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%);
    pointer-events: none;
  }
  .wl-stat::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%);
    pointer-events: none;
  }
  .wl-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .wl-stat-top   { display: flex; align-items: flex-start; justify-content: space-between; }
  .wl-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .wl-stat-icon  { width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 10px rgba(0,0,0,0.3); }
  .wl-stat-value { font-size: 26px; font-weight: 700; color: var(--text-primary); letter-spacing: -1.2px; font-family: var(--font-mono); line-height: 1; }
  .wl-stat-sub   { font-size: 11px; color: var(--text-muted); margin-top: 5px; }

  /* Panel */
  .wl-panel { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .wl-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; padding: 14px 20px; border-bottom: 1px solid var(--border); flex-wrap: wrap;
  }
  .wl-toolbar-left  { display: flex; align-items: center; gap: 10px; }
  .wl-toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .wl-panel-title { font-size: 13px; font-weight: 700; color: var(--text-primary); }
  .wl-count-pill {
    padding: 2px 8px; border-radius: 99px; border: 1px solid var(--border-hi);
    font-size: 10px; color: var(--text-muted); font-family: var(--font-mono);
  }

  /* Filter tabs */
  .wl-filter-tabs {
    display: flex; gap: 2px; padding: 4px;
    background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm);
  }
  .wl-filter-tab {
    padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 500;
    cursor: pointer; transition: all 0.15s; border: none;
    background: none; color: var(--text-muted); font-family: var(--font-sans);
  }
  .wl-filter-tab:hover { color: var(--text-primary); }
  .wl-filter-tab.active { background: var(--accent-dim); color: var(--accent-light); box-shadow: inset 0 0 0 1px rgba(99,102,241,0.2); }

  /* Search */
  .wl-search-wrap { position: relative; }
  .wl-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .wl-search {
    padding: 6px 12px 6px 30px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary);
    font-family: var(--font-sans); width: 210px; outline: none; transition: border-color 0.18s;
  }
  .wl-search::placeholder { color: var(--text-muted); }
  .wl-search:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .wl-table-wrap { overflow-x: auto; }
  .wl-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .wl-table thead tr { border-bottom: 1px solid var(--border); }
  .wl-table th {
    padding: 10px 20px; text-align: left;
    font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase;
    color: var(--text-muted); white-space: nowrap; cursor: pointer; user-select: none; transition: color 0.15s;
  }
  .wl-table th:hover { color: var(--text-secondary); }
  .wl-table th.no-sort { cursor: default; }
  .wl-table tbody tr { border-bottom: 1px solid rgba(255,255,255,0.035); transition: background 0.12s; }
  .wl-table tbody tr:last-child { border-bottom: none; }
  .wl-table tbody tr:hover { background: rgba(99,102,241,0.04); }
  .wl-table td { padding: 14px 20px; vertical-align: middle; }

  /* Avatar */
  .wl-avatar-cell { display: flex; align-items: center; gap: 12px; }
  .wl-avatar {
    width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; border: 1.5px solid rgba(99,102,241,0.25);
  }
  .wl-avatar-indigo { background: rgba(99,102,241,0.15);  color: #818cf8; }
  .wl-avatar-green  { background: rgba(52,211,153,0.15);  color: #34d399; }
  .wl-avatar-amber  { background: rgba(251,191,36,0.15);  color: #fbbf24; }
  .wl-avatar-rose   { background: rgba(251,113,133,0.15); color: #fb7185; }
  .wl-avatar-purple { background: rgba(167,139,250,0.15); color: #a78bfa; }
  .wl-avatar-cyan   { background: rgba(34,211,238,0.15);  color: #22d3ee; }
  .wl-cell-name  { font-size: 13px; font-weight: 500; color: var(--text-primary); }
  .wl-cell-email { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

  /* Balance cell */
  .wl-balance-pos { font-size: 15px; font-weight: 700; color: var(--text-primary); font-family: var(--font-mono); }
  .wl-balance-zero { font-size: 15px; font-weight: 600; color: var(--text-muted); font-family: var(--font-mono); }

  /* Currency badge */
  .wl-currency {
    display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 6px;
    background: rgba(255,255,255,0.04); border: 1px solid var(--border-hi);
    font-size: 10.5px; font-weight: 600; color: var(--text-secondary); letter-spacing: 0.5px;
    font-family: var(--font-mono);
  }

  /* Status chip */
  .wl-chip {
    display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 99px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.4px; text-transform: capitalize; border: 1px solid;
  }
  .wl-chip.active    { background: var(--green-dim);  color: var(--green);  border-color: rgba(52,211,153,0.2);  }
  .wl-chip.suspended { background: var(--rose-dim);   color: var(--rose);   border-color: rgba(251,113,133,0.2); }
  .wl-chip.inactive  { background: rgba(255,255,255,0.04); color: var(--text-muted); border-color: var(--border); }

  /* Date cell */
  .wl-date { font-size: 11px; color: var(--text-muted); font-family: var(--font-mono); white-space: nowrap; }

  /* Action btn */
  .wl-adjust-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 12px; border-radius: var(--radius-sm);
    background: none; border: 1px solid var(--border-hi);
    color: var(--text-secondary); font-size: 11.5px; font-weight: 500;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .wl-adjust-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); box-shadow: 0 0 12px rgba(99,102,241,0.1); }

  /* Pagination */
  .wl-pagination {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 20px; border-top: 1px solid var(--border);
  }
  .wl-pag-info  { font-size: 11px; color: var(--text-muted); font-family: var(--font-mono); }
  .wl-pag-pages { display: flex; gap: 4px; }
  .wl-pag-btn {
    width: 28px; height: 28px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-family: var(--font-mono);
    border: 1px solid var(--border); background: none;
    color: var(--text-muted); cursor: pointer; transition: all 0.15s;
  }
  .wl-pag-btn:hover  { background: var(--bg-hover); color: var(--text-primary); }
  .wl-pag-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Skeleton */
  .wl-skel {
    height: 10px; border-radius: 5px;
    background: linear-gradient(90deg, var(--bg-hover) 25%, rgba(99,102,241,0.05) 50%, var(--bg-hover) 75%);
    background-size: 200% 100%; animation: wl-shimmer 1.8s infinite;
  }
  @keyframes wl-shimmer { 0%{ background-position:200% 0 } 100%{ background-position:-200% 0 } }

  /* Empty */
  .wl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px; gap: 8px; color: var(--text-muted); font-size: 12px; }

  /* Modal overlay */
  .wl-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 50; padding: 16px; backdrop-filter: blur(4px);
  }
  .wl-modal {
    background: var(--bg-elevated); border: 1px solid var(--border-hi);
    border-radius: var(--radius-lg); width: 100%; max-width: 460px;
    display: flex; flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1);
  }
  .wl-modal-head {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 20px 24px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .wl-modal-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .wl-modal-sub   { font-size: 12px; color: var(--text-secondary); margin-top: 3px; }
  .wl-modal-body  { padding: 24px; display: flex; flex-direction: column; gap: 18px; }
  .wl-modal-foot  {
    display: flex; align-items: center; justify-content: flex-end; gap: 8px;
    padding: 16px 24px; border-top: 1px solid var(--border);
  }
  .wl-close-btn {
    width: 28px; height: 28px; border-radius: 7px; background: none; border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .wl-close-btn:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.25); }

  /* Mode selector */
  .wl-mode-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; }
  .wl-mode-btn {
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    padding: 12px 8px; border-radius: var(--radius-sm); border: 1px solid var(--border);
    background: none; color: var(--text-muted); font-size: 11.5px; font-weight: 500;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .wl-mode-btn:hover { border-color: var(--border-hi); color: var(--text-primary); background: var(--bg-hover); }
  .wl-mode-btn.credit.sel   { border-color: rgba(52,211,153,0.35);  background: rgba(52,211,153,0.07);  color: var(--green);  box-shadow: inset 0 0 0 1px rgba(52,211,153,0.15);  }
  .wl-mode-btn.deduct.sel   { border-color: rgba(251,191,36,0.35);  background: rgba(251,191,36,0.07);  color: var(--amber);  box-shadow: inset 0 0 0 1px rgba(251,191,36,0.15);  }
  .wl-mode-btn.withdraw.sel { border-color: rgba(251,113,133,0.35); background: rgba(251,113,133,0.07); color: var(--rose);   box-shadow: inset 0 0 0 1px rgba(251,113,133,0.15); }

  /* Form elements */
  .wl-label { font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; display: block; letter-spacing: 0.2px; }
  .wl-input-wrap { position: relative; }
  .wl-input-prefix { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); font-size: 13px; color: var(--text-muted); pointer-events: none; }
  .wl-input {
    width: 100%; padding: 9px 12px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s;
  }
  .wl-input.has-prefix { padding-left: 24px; }
  .wl-input::placeholder { color: var(--text-muted); }
  .wl-input:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Preview bar */
  .wl-preview {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 16px; border-radius: var(--radius-sm);
    background: rgba(255,255,255,0.025); border: 1px solid var(--border);
  }
  .wl-preview-label { font-size: 11px; color: var(--text-muted); }
  .wl-preview-value { font-size: 14px; font-weight: 700; font-family: var(--font-mono); color: var(--text-primary); }
  .wl-preview-value.negative { color: var(--rose); }

  /* Error banner */
  .wl-error {
    display: flex; align-items: center; gap: 8px; padding: 10px 14px;
    border-radius: var(--radius-sm); background: rgba(251,113,133,0.08);
    border: 1px solid rgba(251,113,133,0.22); color: var(--rose); font-size: 12px;
  }

  /* Modal buttons */
  .wl-ghost-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 16px;
    border-radius: var(--radius-sm); background: none; border: 1px solid var(--border-hi);
    color: var(--text-secondary); font-size: 12px; font-weight: 500;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .wl-ghost-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .wl-submit-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 18px;
    border-radius: var(--radius-sm); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans); border: none;
  }
  .wl-submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .wl-submit-btn.credit   { background: var(--green);  color: #0a0a0a; }
  .wl-submit-btn.deduct   { background: var(--amber);  color: #0a0a0a; }
  .wl-submit-btn.withdraw { background: var(--rose);   color: #fff; }
  .wl-submit-btn.credit:not(:disabled):hover   { box-shadow: 0 0 16px rgba(52,211,153,0.4); }
  .wl-submit-btn.deduct:not(:disabled):hover   { box-shadow: 0 0 16px rgba(251,191,36,0.4); }
  .wl-submit-btn.withdraw:not(:disabled):hover { box-shadow: 0 0 16px rgba(251,113,133,0.4); }

  /* Toast */
  .wl-toast {
    position: fixed; bottom: 24px; right: 24px; z-index: 100;
    display: flex; align-items: center; gap: 10px; padding: 12px 18px;
    border-radius: var(--radius-md); background: var(--bg-elevated);
    border: 1px solid var(--border-hi);
    box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.1);
    font-size: 13px; color: var(--text-primary);
    animation: wl-slide-in 0.22s ease;
  }
  .wl-toast.ok  { border-color: rgba(52,211,153,0.35); }
  .wl-toast.err { border-color: rgba(251,113,133,0.35); }
  @keyframes wl-slide-in { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }

  /* Gate */
  .wl-gate {
    height: 100vh; display: flex; align-items: center; justify-content: center;
    flex-direction: column; gap: 14px; background: var(--bg-base);
  }
  .wl-gate-icon {
    width: 36px; height: 36px; border-radius: 10px;
    background: linear-gradient(135deg,#6366f1,#a78bfa);
    box-shadow: 0 0 24px rgba(99,102,241,0.4);
    display: flex; align-items: center; justify-content: center;
  }

  /* ═══════════════════════════════════════
     MOBILE SIDEBAR OVERLAY + HAMBURGER
  ════════════════════════════════════════ */
  .ad-sidebar-overlay {
    display: none;
    position: fixed; inset: 0; background: rgba(0,0,0,0.6);
    z-index: 40; backdrop-filter: blur(2px);
    animation: overlay-fade-in 0.22s var(--transition-smooth);
  }
  @keyframes overlay-fade-in { from { opacity:0 } to { opacity:1 } }

  .wl-hamburger {
    display: none;
    width: 36px; height: 36px; border-radius: var(--radius-sm);
    background: var(--bg-card); border: 1px solid var(--border-hi);
    color: var(--text-secondary); cursor: pointer;
    align-items: center; justify-content: center;
    transition: all 0.15s; flex-shrink: 0;
  }
  .wl-hamburger:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* ═══════════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ════════════════════════════════════════ */

  /* ── Large desktop (≥1400px) ── */
  @media (min-width: 1400px) {
    .wl-stat-grid { gap: 18px; }
    .wl-main { padding: 32px 36px; }
    .wl-topbar { padding: 0 36px; }
    .wl-stat-value { font-size: 30px; }
  }

  /* ── Medium desktop (1024px – 1279px) ── */
  @media (max-width: 1279px) {
    :root { --sidebar-w: 210px; }
    .wl-main { padding: 22px 22px; }
    .wl-topbar { padding: 0 22px; }
    .wl-stat-grid { gap: 12px; }
  }

  /* ── Tablet landscape (768px – 1023px): icon-strip sidebar ── */
  @media (max-width: 1023px) {
    :root { --sidebar-w: 60px; }

    .ad-logo-text,
    .ad-nav-section-label,
    .ad-nav-link span,
    .ad-nav-chevron,
    .ad-user-info,
    .ad-logout-btn { display: none !important; }

    .ad-logo { justify-content: center; padding: 0; }
    .ad-logo-icon { width: 36px; height: 36px; }
    .ad-nav { padding: 14px 6px; gap: 14px; }
    .ad-nav-items { gap: 2px; }
    .ad-nav-link { justify-content: center; padding: 10px; gap: 0; }
    .ad-nav-link svg { opacity: 0.7; }
    .ad-nav-link.active svg { opacity: 1; }
    .ad-nav-link.active::before { top: 10%; bottom: 10%; }
    .ad-user-card { padding: 10px 6px; }
    .ad-user-inner { justify-content: center; padding: 8px; gap: 0; }

    .wl-stat-grid { grid-template-columns: repeat(2,1fr); }
    .wl-main { padding: 18px 18px; gap: 16px; }
    .wl-topbar { padding: 0 18px; }
    .wl-toolbar { flex-direction: column; align-items: stretch; gap: 10px; }
    .wl-toolbar-right { flex-wrap: wrap; }
    .wl-search { width: 100%; }
    .wl-filter-tabs { flex-wrap: wrap; }
  }

  /* ── Mobile (≤767px): off-canvas drawer ── */
  @media (max-width: 767px) {
    :root { --sidebar-w: 232px; }

    .wl-hamburger { display: flex; }

    .ad-sidebar {
      position: fixed; top: 0; left: 0; height: 100vh;
      z-index: 50; transform: translateX(-100%);
      transition: transform 0.28s var(--transition-smooth);
      box-shadow: 4px 0 40px rgba(0,0,0,0.5);
      overflow: hidden;
    }
    .ad-sidebar.open { transform: translateX(0); }
    .ad-sidebar-overlay.open { display: block; }

    /* Restore all hidden items inside the open mobile drawer */
    .ad-sidebar.open .ad-logo-text,
    .ad-sidebar.open .ad-nav-section-label,
    .ad-sidebar.open .ad-nav-link span,
    .ad-sidebar.open .ad-nav-chevron,
    .ad-sidebar.open .ad-user-info,
    .ad-sidebar.open .ad-logout-btn { display: initial !important; }

    .ad-sidebar.open .ad-sidebar-close { display: flex !important; }
    .ad-sidebar.open .ad-logo { justify-content: flex-start; padding: 0 18px; }
    .ad-sidebar.open .ad-nav { padding: 18px 10px; gap: 22px; }
    .ad-sidebar.open .ad-nav-link { justify-content: flex-start; padding: 8px 10px; gap: 9px; }
    .ad-sidebar.open .ad-nav-link span { display: block !important; flex: 1; }
    .ad-sidebar.open .ad-nav-section-label { display: block !important; }
    .ad-sidebar.open .ad-user-inner { justify-content: flex-start; gap: 10px; padding: 9px 10px; }

    .wl-shell { overflow: hidden; }
    .wl-page { width: 100%; }

    .wl-stat-grid { grid-template-columns: repeat(2,1fr); gap: 10px; }
    .wl-stat-value { font-size: 20px; }
    .wl-stat { padding: 14px; }
    .wl-stat-icon { width: 28px; height: 28px; }

    .wl-main { padding: 14px 12px; gap: 14px; }
    .wl-topbar { padding: 0 14px; }
    .wl-topbar-sub { display: none; }
    .wl-topbar-title { font-size: 13px; }

    .wl-toolbar { padding: 10px 14px; flex-direction: column; align-items: stretch; gap: 10px; }
    .wl-toolbar-right { flex-direction: column; align-items: stretch; gap: 8px; }
    .wl-search { width: 100%; }
    .wl-filter-tabs { flex-wrap: wrap; }

    .wl-table th, .wl-table td { padding: 10px 12px; }
    /* Hide less critical columns on small screens */
    .wl-table th:nth-child(3), .wl-table td:nth-child(3),
    .wl-table th:nth-child(4), .wl-table td:nth-child(4) { display: none; }

    .wl-overlay { align-items: flex-end; padding: 0; }
    .wl-modal { border-radius: var(--radius-md) var(--radius-md) 0 0; max-width: 100%; }

    .wl-pagination { flex-direction: column; align-items: flex-start; gap: 10px; padding: 10px 14px; }
    .wl-pag-pages { flex-wrap: wrap; }

    .wl-toast { bottom: 16px; right: 12px; left: 12px; font-size: 12px; }

    .wl-topbar-badge { display: none; }
  }

  /* ── Small mobile (≤480px) ── */
  @media (max-width: 480px) {
    .wl-stat-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .wl-stat-value { font-size: 18px; }
    .wl-stat-sub { font-size: 9px; }
    .wl-stat { padding: 12px; gap: 10px; }
    .wl-cell-email { display: none; }
    .wl-avatar { width: 28px; height: 28px; font-size: 9px; }
    .wl-avatar-cell { gap: 8px; }
  }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtCurrency(val) {
  if (val == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(val);
}
function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function initials(first, last) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
}
const PALETTE = ["indigo", "green", "amber", "rose", "purple", "cyan"];
function avatarColor(id) {
  if (!id) return PALETTE[0];
  const n = typeof id === "string" ? id.charCodeAt(0) + id.charCodeAt(id.length - 1) : id;
  return PALETTE[n % PALETTE.length];
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Sort icon ────────────────────────────────────────────────────────────────
function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
  return sortDir === 1
    ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
    : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
}

// ─── Adjust Modal ─────────────────────────────────────────────────────────────
function AdjustModal({ wallet, onClose, onSuccess }) {
  const [mode,    setMode]    = useState("credit");
  const [amount,  setAmount]  = useState("");
  const [note,    setNote]    = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const MODES = [
    { key: "credit",   label: "Credit",   icon: ArrowUpRight   },
    { key: "deduct",   label: "Deduct",   icon: Minus          },
    { key: "withdraw", label: "Withdraw", icon: ArrowDownRight },
  ];

  const current = wallet?.balance ?? 0;
  const parsed  = parseFloat(amount) || 0;
  const preview = mode === "credit" ? current + parsed : current - parsed;

  async function handleSubmit() {
    setError("");
    if (!parsed || parsed <= 0) { setError("Enter a valid amount."); return; }
    if (mode !== "credit" && parsed > current) { setError("Amount exceeds current balance."); return; }
    setLoading(true);
    try {
      const newBalance = Math.max(0, preview);
      let walletId = wallet._synthetic ? null : wallet.id;

      if (walletId) {
        // Existing wallet — update balance
        const { error: walletErr } = await supabase
          .from("wallets")
          .update({ balance: newBalance, updated_at: new Date().toISOString() })
          .eq("id", walletId);
        if (walletErr) throw walletErr;
      } else {
        // No wallet row yet — insert one for this user
        const { data: inserted, error: insertErr } = await supabase
          .from("wallets")
          .insert({
            user_id: wallet.user_id,
            balance: newBalance,
            currency: wallet.currency ?? "USD",
            updated_at: new Date().toISOString(),
            created_at: new Date().toISOString(),
          })
          .select("id")
          .single();
        if (insertErr) throw insertErr;
        walletId = inserted.id;
      }

      await supabase.from("transactions").insert({
        user_id:   wallet.user_id,
        wallet_id: walletId,
        type:      mode === "credit" ? "deposit" : "withdrawal",
        amount:    parsed,
        status:    "approved",
        note:      note || `Admin ${mode} — manual adjustment`,
        created_at: new Date().toISOString(),
      });

      onSuccess?.(`Balance ${mode}ed successfully.`);
      onClose();
    } catch (e) {
      setError(e.message ?? "Failed to update balance.");
    } finally {
      setLoading(false);
    }
  }

  const user = wallet?.users;

  return (
    <div className="wl-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wl-modal">
        {/* Head */}
        <div className="wl-modal-head">
          <div>
            <div className="wl-modal-title">Adjust wallet</div>
            <div className="wl-modal-sub">
              {user?.first_name} {user?.last_name} · Current: <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>{fmtCurrency(current)}</span>
            </div>
          </div>
          <button className="wl-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        {/* Body */}
        <div className="wl-modal-body">
          {/* Mode selector */}
          <div className="wl-mode-grid">
            {MODES.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setMode(key)}
                className={`wl-mode-btn ${key}${mode === key ? " sel" : ""}`}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>

          {/* Amount */}
          <div>
            <label className="wl-label">Amount (USD)</label>
            <div className="wl-input-wrap">
              <span className="wl-input-prefix">$</span>
              <input
                type="number" min="0" step="0.01"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="wl-input has-prefix"
                style={{ fontFamily: "var(--font-mono)" }}
              />
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="wl-label">Note (optional)</label>
            <input
              type="text" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for adjustment…"
              className="wl-input"
            />
          </div>

          {/* Preview */}
          {parsed > 0 && (
            <div className="wl-preview">
              <span className="wl-preview-label">New balance</span>
              <span className={`wl-preview-value${preview < 0 ? " negative" : ""}`}>
                {fmtCurrency(Math.max(0, preview))}
              </span>
            </div>
          )}

          {error && (
            <div className="wl-error">
              <AlertCircle size={13} style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="wl-modal-foot">
          <button className="wl-ghost-btn" onClick={onClose}>Cancel</button>
          <button
            className={`wl-submit-btn ${mode}`}
            disabled={loading || !parsed}
            onClick={handleSubmit}
          >
            {loading ? "Processing…" : `Confirm ${mode}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Wallets() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [sessionReady,  setSessionReady]  = useState(false);

  const [wallets,      setWallets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [sortField,    setSortField]    = useState("balance");
  const [sortDir,      setSortDir]      = useState(-1);
  const [page,         setPage]         = useState(1);
  const [selected,     setSelected]     = useState(null);
  const [toast,        setToast]        = useState({ msg: "", ok: true });
  const [balanceFilter, setBalFil]      = useState("all");
  const [sidebarOpen,  setSidebarOpen]  = useState(false);

  // ── Session gate ────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchWallets = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all users first — this is the source of truth for the table
      const { data: userRows, error: userErr } = await supabase
        .from("users")
        .select("id, first_name, last_name, email, status");
      if (userErr) throw userErr;

      // Fetch wallets separately; if RLS blocks it or the table is empty, treat as []
      // Log the real error so it's visible in the console for debugging
      const { data: walletRows, error: walletErr } = await supabase
        .from("wallets")
        .select("id, user_id, balance, currency, updated_at, created_at");
      if (walletErr) console.warn("[Wallets] wallet fetch error:", walletErr.message, walletErr.details);

      // Merge: every user appears; wallet-less users get balance 0
      const walletMap = {};
      (walletRows ?? []).forEach((w) => { walletMap[w.user_id] = w; });
      const merged = (userRows ?? []).map((u) => {
        const w = walletMap[u.id];
        return w
          ? { ...w, users: u }
          : { id: `synthetic-${u.id}`, user_id: u.id, balance: 0, currency: "USD", updated_at: null, created_at: null, users: u, _synthetic: true };
      });
      setWallets(merged);
    } catch (e) {
      console.error("[Wallets] fetch failed:", e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (sessionReady) fetchWallets(); }, [sessionReady, fetchWallets]);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3200);
  }

  async function onAdjustSuccess(msg) {
    showToast(msg);
    await fetchWallets();
  }

  // ── Stats ────────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total   = wallets.reduce((s, w) => s + (w.balance ?? 0), 0);
    const funded  = wallets.filter((w) => (w.balance ?? 0) > 0).length;
    const empty   = wallets.filter((w) => (w.balance ?? 0) === 0).length;
    const highest = wallets.reduce((m, w) => Math.max(m, w.balance ?? 0), 0);
    return { total, funded, empty, highest, count: wallets.length };
  }, [wallets]);

  const STAT_CARDS = [
    { label: "Platform balance", value: fmtCurrency(stats.total),   icon: DollarSign,  bg: "rgba(52,211,153,0.15)",  color: "var(--green)",  glow: "rgba(52,211,153,0.08)",  bar: "var(--green)",  sub: `Across ${stats.count} wallets` },
    { label: "Funded wallets",   value: stats.funded,               icon: TrendingUp,  bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)", bar: "var(--accent)", sub: "Balance > $0" },
    { label: "Empty wallets",    value: stats.empty,                icon: TrendingDown,bg: "rgba(251,191,36,0.15)",  color: "var(--amber)", glow: "rgba(251,191,36,0.08)",  bar: "var(--amber)",  sub: "Balance = $0" },
    { label: "Highest balance",  value: fmtCurrency(stats.highest), icon: Wallet,      bg: "rgba(167,139,250,0.15)", color: "var(--purple)", glow: "rgba(167,139,250,0.08)", bar: "var(--purple)", sub: "Single wallet" },
  ];

  // ── Filter + sort ────────────────────────────────────────────────────────────
  const BALANCE_FILTERS = [
    { key: "all",     label: "All" },
    { key: "funded",  label: "Funded" },
    { key: "empty",   label: "Empty" },
  ];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = wallets.filter((w) => {
      const matchSearch = !q
        || `${w.users?.first_name ?? ""} ${w.users?.last_name ?? ""}`.toLowerCase().includes(q)
        || (w.users?.email ?? "").toLowerCase().includes(q);
      const bal = w.balance ?? 0;
      const matchBal = balanceFilter === "all"
        || (balanceFilter === "funded" && bal > 0)
        || (balanceFilter === "empty"  && bal === 0);
      return matchSearch && matchBal;
    });

    list.sort((a, b) => {
      if (sortField === "name") {
        const an = `${a.users?.first_name ?? ""}${a.users?.last_name ?? ""}`;
        const bn = `${b.users?.first_name ?? ""}${b.users?.last_name ?? ""}`;
        return an.localeCompare(bn) * sortDir;
      }
      if (sortField === "updated") return (new Date(a.updated_at ?? 0) - new Date(b.updated_at ?? 0)) * sortDir;
      return ((a.balance ?? 0) - (b.balance ?? 0)) * sortDir;
    });
    return list;
  }, [wallets, search, balanceFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSort(field) {
    if (field === sortField) setSortDir((d) => d * -1);
    else { setSortField(field); setSortDir(-1); }
    setPage(1);
  }

  const COLS = [
    { key: "name",    label: "User"         },
    { key: "balance", label: "Balance"      },
    { key: "updated", label: "Last updated" },
  ];

  if (!sessionReady) {
    return (
      <div className="wl-gate">
        <StyleInjector />
        <div className="wl-gate-icon"><Wallet size={16} color="#fff" /></div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-sans)" }}>Verifying session…</p>
      </div>
    );
  }

  return (
    <div className="wl-shell">
      <StyleInjector />

      {/* ── Mobile sidebar overlay ── */}
      <div
        className={`ad-sidebar-overlay${sidebarOpen ? " open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Sidebar ── */}
      <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon">
            <CandlestickChart size={16} color="#fff" />
          </div>
          <div className="ad-logo-text">
            <strong>vantrexmarket</strong>
            <span>Admin panel</span>
          </div>
          <button
            className="ad-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            style={{
              marginLeft: "auto", display: "none",
              width: 28, height: 28, borderRadius: 7,
              background: "none", border: "1px solid var(--border)",
              color: "var(--text-muted)", cursor: "pointer",
              alignItems: "center", justifyContent: "center",
              flexShrink: 0, transition: "all 0.15s",
            }}
          >
            <X size={13} />
          </button>
        </div>

        <nav className="ad-nav" onClick={() => setSidebarOpen(false)}>
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="ad-nav-section-label">{section.label}</p>
              <div className="ad-nav-items">
                {section.items.map((item) => (
                  <SidebarLink key={item.to} {...item} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info">
              <strong>Super Admin</strong>
              <span>{adminEmail}</span>
            </div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Page ── */}
      <div className="wl-page">

        {/* Topbar */}
        <header className="wl-topbar">
          <div className="wl-topbar-left">
            <button className="wl-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="3" width="14" height="1.5" rx="0.75" fill="currentColor"/>
                <rect x="1" y="7.25" width="14" height="1.5" rx="0.75" fill="currentColor"/>
                <rect x="1" y="11.5" width="10" height="1.5" rx="0.75" fill="currentColor"/>
              </svg>
            </button>
            <div className="wl-topbar-badge"><Wallet size={9} />Wallets</div>
            <div>
              <div className="wl-topbar-title">Wallet management</div>
              <div className="wl-topbar-sub">Monitor and adjust user balances</div>
            </div>
          </div>
          <div className="wl-topbar-right">
            <button className="wl-btn" onClick={fetchWallets}>
              <RefreshCw size={12} />Refresh
            </button>
          </div>
        </header>

        {/* Main */}
        <main className="wl-main">

          {/* Stat cards */}
          <div className="wl-stat-grid">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
              <div key={label} className="wl-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                <div className="wl-stat-top">
                  <span className="wl-stat-label">{label}</span>
                  <div className="wl-stat-icon" style={{ background: bg }}>
                    <Icon size={15} style={{ color }} />
                  </div>
                </div>
                <div>
                  <div className="wl-stat-value">{value}</div>
                  {sub && <div className="wl-stat-sub">{sub}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Table panel */}
          <div className="wl-panel">

            {/* Toolbar */}
            <div className="wl-toolbar">
              <div className="wl-toolbar-left">
                <span className="wl-panel-title">All wallets</span>
                <span className="wl-count-pill">{filtered.length}</span>
              </div>
              <div className="wl-toolbar-right">
                {/* Balance filter */}
                <div className="wl-filter-tabs">
                  {BALANCE_FILTERS.map(({ key, label }) => (
                    <button
                      key={key}
                      className={`wl-filter-tab${balanceFilter === key ? " active" : ""}`}
                      onClick={() => { setBalFil(key); setPage(1); }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {/* Search */}
                <div className="wl-search-wrap">
                  <Search size={12} className="wl-search-icon" />
                  <input
                    type="text" placeholder="Search user…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="wl-search"
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="wl-table-wrap">
              <table className="wl-table">
                <thead>
                  <tr>
                    {COLS.map(({ key, label }) => (
                      <th key={key} onClick={() => handleSort(key)}>
                        {label}<SortIcon field={key} sortField={sortField} sortDir={sortDir} />
                      </th>
                    ))}
                    <th className="no-sort">Currency</th>
                    <th className="no-sort">User status</th>
                    <th className="no-sort" style={{ textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 6 }).map((_, j) => (
                          <td key={j}><div className="wl-skel" style={{ width: `${45 + (i * j * 13) % 40}%` }} /></td>
                        ))}
                      </tr>
                    ))
                  ) : pageSlice.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="wl-empty">
                          <AlertCircle size={22} />
                          <span>No wallets found</span>
                        </div>
                      </td>
                    </tr>
                  ) : pageSlice.map((w) => {
                    const user   = w.users;
                    const bal    = w.balance ?? 0;
                    const aColor = avatarColor(w.user_id);
                    const status = user?.status ?? "inactive";
                    return (
                      <tr key={w.user_id}>
                        {/* User */}
                        <td>
                          <div className="wl-avatar-cell">
                            <div className={`wl-avatar wl-avatar-${aColor}`}>
                              {initials(user?.first_name, user?.last_name)}
                            </div>
                            <div>
                              <div className="wl-cell-name">{user?.first_name} {user?.last_name}</div>
                              <div className="wl-cell-email">{user?.email}</div>
                            </div>
                          </div>
                        </td>
                        {/* Balance */}
                        <td>
                          <span className={bal > 0 ? "wl-balance-pos" : "wl-balance-zero"}>
                            {fmtCurrency(bal)}
                          </span>
                        </td>
                        {/* Last updated */}
                        <td><span className="wl-date">{fmtDate(w.updated_at)}</span></td>
                        {/* Currency */}
                        <td><span className="wl-currency">{w.currency ?? "USD"}</span></td>
                        {/* User status */}
                        <td>
                          <span className={`wl-chip ${status}`}>
                            {status === "active"
                              ? <CheckCircle2 size={9} />
                              : status === "suspended"
                              ? <XCircle size={9} />
                              : <AlertCircle size={9} />}
                            {status}
                          </span>
                        </td>
                        {/* Action */}
                        <td style={{ textAlign: "right" }}>
                          <button className="wl-adjust-btn" onClick={() => setSelected(w)}>
                            <Wallet size={11} />Adjust
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="wl-pagination">
                <span className="wl-pag-info">
                  {filtered.length
                    ? `${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filtered.length)} of ${filtered.length}`
                    : "0 results"}
                </span>
                <div className="wl-pag-pages">
                  {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      className={`wl-pag-btn${page === p ? " active" : ""}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Adjust modal */}
      {selected && (
        <AdjustModal
          wallet={selected}
          onClose={() => setSelected(null)}
          onSuccess={onAdjustSuccess}
        />
      )}

      {/* Toast */}
      {toast.msg && (
        <div className={`wl-toast ${toast.ok ? "ok" : "err"}`}>
          {toast.ok
            ? <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
            : <XCircle      size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}